import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { Button } from "./ui/button";
import {
  LayoutDashboard, Briefcase, Map as MapIcon, Users, FileText, FolderOpen,
  Receipt, MessageCircle, Settings, Clock, LogOut, Moon, Sun, Zap, Crown, Bookmark, Home
} from "lucide-react";

const items = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", staffOnly: true },
  { to: "/portal", label: "Mein Portal", icon: Home, testid: "nav-portal", customerOnly: true },
  { to: "/agency", label: "Agentur", icon: Crown, testid: "nav-agency", adminOnly: true },
  { to: "/jobs", label: "Einsätze", icon: Briefcase, testid: "nav-jobs", staffOnly: true },
  { to: "/map", label: "Karte", icon: MapIcon, testid: "nav-map", staffOnly: true },
  { to: "/tours", label: "Touren", icon: Bookmark, testid: "nav-tours", staffOnly: true },
  { to: "/team", label: "Mitarbeiter", icon: Users, testid: "nav-team", adminOnly: true },
  { to: "/reports", label: "Berichte", icon: FileText, testid: "nav-reports", staffOnly: true },
  { to: "/documents", label: "Dokumente", icon: FolderOpen, testid: "nav-documents" },
  { to: "/invoices", label: "Rechnungen", icon: Receipt, testid: "nav-invoices" },
  { to: "/time", label: "Zeiterfassung", icon: Clock, testid: "nav-time", staffOnly: true },
  { to: "/chat", label: "Chat", icon: MessageCircle, testid: "nav-chat" },
  { to: "/settings", label: "Einstellungen", icon: Settings, testid: "nav-settings" },
];

function visibleFor(role) {
  return items.filter(it => {
    if (it.adminOnly && role !== "admin") return false;
    if (it.staffOnly && role === "customer") return false;
    if (it.customerOnly && role !== "customer") return false;
    return true;
  });
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex bg-background text-foreground bg-grain">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-border/60 glass flex-col sticky top-0 h-screen z-10">
        <div className="px-6 pt-7 pb-5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <div className="font-heading text-xl font-bold tracking-tight">Navero</div>
            <div className="text-xs text-muted-foreground">Field Service OS</div>
          </div>
        </div>
        <nav className="px-3 py-2 flex-1 space-y-1">
          {visibleFor(user?.role).map(it => (
            <NavLink key={it.to} to={it.to} end={it.to === "/" || it.to === "/portal"} data-testid={it.testid}
              className={({isActive}) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-medium transition-all duration-200
                ${isActive
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`
              }>
              <it.icon className="w-5 h-5" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border/60">
          <div className="flex items-center gap-3 px-3 py-2 rounded-2xl hover:bg-secondary/60 transition">
            <div className="w-9 h-9 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <Button variant="ghost" size="sm" className="flex-1 rounded-full" onClick={toggle} data-testid="theme-toggle">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" className="flex-1 rounded-full" onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 glass border-b border-border/60 z-20 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><Zap className="w-4 h-4" /></div>
          <span className="font-heading font-bold">Navero</span>
        </div>
        <Button variant="ghost" size="icon" className="rounded-full" onClick={toggle} data-testid="theme-toggle-mobile">
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </Button>
      </div>

      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-6 md:py-10">
          <Outlet />
        </div>
        {/* Bottom nav on mobile */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 glass border-t border-border/60 z-20 px-2 py-2 flex items-center gap-1 overflow-x-auto">
          {visibleFor(user?.role).slice(0,6).map(it => (
            <NavLink key={it.to} to={it.to} end={it.to === "/" || it.to === "/portal"} data-testid={`${it.testid}-mobile`}
              className={({isActive}) => `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl text-[10px] font-medium min-w-[60px]
              ${isActive ? "text-primary" : "text-muted-foreground"}`}>
              <it.icon className="w-5 h-5" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>
      </main>
    </div>
  );
}
